import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import EmployerRoute from "@/components/EmployerRoute";
import { LazyPerformanceTracker } from "@/components/lazy/LazyAnalytics";

// Eager load critical pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Jobs from "./pages/Jobs";

// Lazy load secondary pages
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Companies = lazy(() => import("./pages/Companies"));
const PostJob = lazy(() => import("./pages/PostJob"));
const PricingPlans = lazy(() => import("./pages/PricingPlans"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PaymentConfirmation = lazy(() => import("./pages/PaymentConfirmation"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const ATS = lazy(() => import("./pages/ATS"));
const JobManagement = lazy(() => import("./pages/JobManagement"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const JobAlerts = lazy(() => import("./pages/JobAlerts"));
const CandidateSearch = lazy(() => import("./pages/CandidateSearch"));
const SuperAdmin = lazy(() => import("./pages/SuperAdmin"));
const ATSIntegration = lazy(() => import("./pages/ATSIntegration"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Safety = lazy(() => import("./pages/Safety"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OnboardingJobSeeker = lazy(() => import("./pages/OnboardingJobSeeker"));
const OnboardingEmployer = lazy(() => import("./pages/OnboardingEmployer"));
const OnboardingConfirmation = lazy(() => import("./pages/OnboardingConfirmation"));
const EmailConfirmation = lazy(() => import("./pages/EmailConfirmation"));
const AdminMetrics = lazy(() => import("./pages/AdminMetrics"));
const PublicBrandingPage = lazy(() => import("./pages/PublicBrandingPage"));
const TeamInvitation = lazy(() => import("./pages/TeamInvitation"));
const TeamInvitationProcess = lazy(() => import("./pages/TeamInvitationProcess"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <AuthProvider>
        <NotificationProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
          <BrowserRouter>
            <Suspense fallback={null}>
              <LazyPerformanceTracker />
            </Suspense>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/jobs/:id" element={<JobDetail />} />
                <Route path="/companies" element={<Companies />} />
                <Route path="/post-job" element={<EmployerRoute><PostJob /></EmployerRoute>} />
                <Route path="/pricing-plans" element={<PricingPlans />} />
                <Route path="/pricing" element={<PricingPlans />} />
                <Route path="/checkout" element={<EmployerRoute><Checkout /></EmployerRoute>} />
                <Route path="/payment-confirmation" element={<EmployerRoute><PaymentConfirmation /></EmployerRoute>} />
                <Route path="/payment-success" element={<EmployerRoute><PaymentSuccess /></EmployerRoute>} />
                <Route path="/ats" element={<EmployerRoute><ATS /></EmployerRoute>} />
                <Route path="/ats/:jobId" element={<EmployerRoute><ATS /></EmployerRoute>} />
                <Route path="/job-management" element={<EmployerRoute><JobManagement /></EmployerRoute>} />
                <Route path="/job-alerts" element={<JobAlerts />} />
                <Route path="/candidate-search" element={<EmployerRoute><CandidateSearch /></EmployerRoute>} />
                <Route path="/account-settings" element={<AccountSettings />} />
                <Route path="/super-admin" element={<SuperAdmin />} />
                <Route path="/ats-integration" element={<ATSIntegration />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/safety" element={<Safety />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/email-confirmation" element={<EmailConfirmation />} />
                <Route path="/onboarding" element={<OnboardingConfirmation />} />
                <Route path="/onboarding/job-seeker" element={<OnboardingJobSeeker />} />
                <Route path="/onboarding/employer" element={<OnboardingEmployer />} />
                <Route path="/company/:slug" element={<PublicBrandingPage />} />
                <Route path="/admin/metrics" element={<AdminMetrics />} />
                <Route path="/team-invitation" element={<TeamInvitation />} />
                <Route path="/team-invitation/process" element={<TeamInvitationProcess />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </NotificationProvider>
    </AuthProvider>
  </LanguageProvider>
</QueryClientProvider>
);

export default App;
